/**
 * Infobulle immédiate en portal (déborde des conteneurs overflow).
 */

import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

const PANEL_GAP = 8;
const PANEL_MAX_W = 280;

export default function DiffereStatsHoverTip({
  title,
  children,
  body,
}: {
  title?: ReactNode;
  body: ReactNode;
  children: ReactNode;
}) {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  const place = useCallback(() => {
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    const pw = panel?.offsetWidth ?? Math.min(PANEL_MAX_W, 200);
    const ph = panel?.offsetHeight ?? 80;
    let left = r.left + r.width / 2 - pw / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - pw - 8));
    let top = r.top - ph - PANEL_GAP;
    if (top < 8) {
      top = r.bottom + PANEL_GAP;
    }
    if (top + ph > window.innerHeight - 8) {
      top = Math.max(8, window.innerHeight - ph - 8);
    }
    setCoords({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    place();
    const onScroll = () => place();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, place, title, body]);

  return (
    <span
      ref={anchorRef}
      className="differe-stats-hover-tip"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      {children}
      {open && createPortal(
        <span
          ref={panelRef}
          className="differe-stats-hover-tip-panel differe-stats-hover-tip-panel--portal"
          role="tooltip"
          style={coords
            ? { top: coords.top, left: coords.left, opacity: 1, visibility: 'visible' }
            : { top: -9999, left: -9999, opacity: 0, visibility: 'hidden' }}
        >
          {title != null && <span className="differe-stats-hover-tip-title">{title}</span>}
          <span className="differe-stats-hover-tip-body">{body}</span>
        </span>,
        document.body,
      )}
    </span>
  );
}
